import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { ListRowActions } from "shared/ListRowActions";
import { TagBubble } from "shared/TagBubble";
import formatDate from "shared/@utils/formatDate";
import styles from "./DisclosureListItem.module.css";

export function DisclosureListItem({
  title,
  thumbnailPath,
  publishDate,
  mediaUsedOn,
  type,
  status,
  tags,
  onDuplicate,
  onArchive,
  onEdit,
}) {
  const tagRowRef = useRef(null);
  const tagMeasureRef = useRef(null);
  const [showTagOverflowDots, setShowTagOverflowDots] = useState(false);
  const [visibleTagCount, setVisibleTagCount] = useState(tags.length);
  const isArchived = status.toLowerCase() === "archived";

  useEffect(() => {
    const updateOverflowState = () => {
      if (!tagRowRef.current || !tagMeasureRef.current || !tags.length) {
        setShowTagOverflowDots(false);
        setVisibleTagCount(tags.length);
        return;
      }

      const rowWidth = tagRowRef.current.clientWidth;
      const tagWidths = Array.from(tagMeasureRef.current.children).map(
        (node) => node.offsetWidth,
      );
      const gap = 6;
      const overflowDotsWidth = 25;
      let used = 0;
      let visible = 0;

      for (let index = 0; index < tagWidths.length; index += 1) {
        const nextWidth = (visible > 0 ? gap : 0) + tagWidths[index];
        if (used + nextWidth > rowWidth) break;
        used += nextWidth;
        visible += 1;
      }

      const hasOverflow = visible < tags.length;
      if (hasOverflow) {
        while (visible > 1 && used + overflowDotsWidth > rowWidth) {
          used -= tagWidths[visible - 1];
          if (visible > 1) used -= gap;
          visible -= 1;
        }
      }

      setVisibleTagCount(Math.max(visible, hasOverflow ? 1 : 0));
      setShowTagOverflowDots(hasOverflow);
    };

    updateOverflowState();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(updateOverflowState);
      if (tagRowRef.current) observer.observe(tagRowRef.current);
    } else {
      window.addEventListener("resize", updateOverflowState);
    }

    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", updateOverflowState);
    };
  }, [tags]);

  return (
    <article className={styles.container} aria-label={`Disclosure ${title}`}>
      <div className={styles.summaryRow}>
        {thumbnailPath ? (
          <img
            src={thumbnailPath}
            alt={`${title} thumbnail`}
            className={styles.thumbnail}
          />
        ) : (
          <div className={styles.thumbnailEmpty} aria-label="No thumbnail">
            No Image
          </div>
        )}

        <div className={styles.content}>
          <div className={styles.infoCluster}>
            <div className={styles.details}>
              <p className={styles.title} title={title}>
                {title}
              </p>
              <p className={styles.date}>
                {publishDate ? formatDate(publishDate, "M/DD/YYYY") : ""}
              </p>
            </div>

            <div className={styles.valueGrid}>
              <p>{mediaUsedOn}</p>
              <p>{type}</p>
              <p className={isArchived ? styles.archivedStatus : undefined}>
                {status}
              </p>
            </div>
          </div>

          <div className={styles.actions}>
            <ListRowActions
              onDuplicate={onDuplicate}
              onArchive={onArchive}
              onEdit={onEdit}
              isArchived={isArchived}
            />
          </div>
        </div>
      </div>

      {!!tags.length && (
        <>
          <div
            className={styles.tagMeasureRow}
            ref={tagMeasureRef}
            aria-hidden="true"
          >
            {tags.map((tag, index) => (
              <TagBubble key={`${tag}-${index}`} label={tag} />
            ))}
          </div>

          <div className={styles.tagRow} ref={tagRowRef}>
            <div className={styles.tagTrack}>
              {tags.slice(0, visibleTagCount).map((tag, index) => (
                <TagBubble key={`${tag}-${index}`} label={tag} />
              ))}
            </div>

            {showTagOverflowDots && (
              <span className={styles.overflowDots} aria-hidden="true">
                <span className={styles.overflowDot} />
                <span className={styles.overflowDot} />
                <span className={styles.overflowDot} />
              </span>
            )}
          </div>
        </>
      )}
    </article>
  );
}

DisclosureListItem.propTypes = {
  title: PropTypes.string.isRequired,
  thumbnailPath: PropTypes.string,
  publishDate: PropTypes.string.isRequired,
  mediaUsedOn: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["Audio", "Video"]).isRequired,
  status: PropTypes.oneOf(["Published", "In Review", "Archived"]).isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
  onDuplicate: PropTypes.func,
  onArchive: PropTypes.func,
  onEdit: PropTypes.func,
};

DisclosureListItem.defaultProps = {
  thumbnailPath: "",
  tags: [],
  onDuplicate: undefined,
  onArchive: undefined,
  onEdit: undefined,
};

export default DisclosureListItem;
